import { useEffect, useMemo } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { Box, Flex } from '@enechain/ecloud-designsystem'
import { FaGearRegular } from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { IconOnlyButton } from '~/components/common/IconOnlyButton'
import type { MultiSelectOption } from '~/components/common/MultiSelect'
import { MultiSelect } from '~/components/common/MultiSelect'
import { iconSize } from '~/constants/iconSize'
import { useBusinessAreaFilter } from '~/hooks/useBusinessAreaFilter'
import { JEPX_AREA_CODES, convertAreaLabel } from '~/models/area'
import { routePaths } from '~/routes/routePaths'

/**
 * エリアフィルタ (MultiSelect) の幅。 design `intraday.html` の
 * `#intraday-area-ms` (`style="min-width:240px;max-width:520px;"`) 準拠。
 */
const AREA_FILTER_MIN_WIDTH = '240px'
const AREA_FILTER_MAX_WIDTH = '520px'

type Props = {
  /**
   * 選択中エリアの実集合 (= 親が持つ AC-BIN-102 全体フィルタの state。 sentinel なし)。
   *
   * AC-BIN-018「エリアフィルタ (すべて + 9エリア個別、 複数選択) / 既定値: すべて」 を、
   * **全 9 エリア = 「すべて」**、 それ以外 = 選択したエリアの実集合として表す。
   * widget 上の「空選択 ⇄ 全 9 エリア」 の正規化 (= design intraday.html の
   * `__intradaySelectedAreas` と同じ「空選択 = 全エリア」 規約) は本 component の
   * 中だけで閉じるので、 **本 component から空集合が出ることはない**
   * (下流の 0 件選択状態は別経路のためのもの。 AC-BIN-012b / 012c)。
   */
  selectedAreas: Set<AreaCode>
  onAreasChange: (next: Set<AreaCode>) => void
  /**
   * 選択状態を保ったまま操作を受け付けない表示専用にする (板発注画面、 AC-BIN-103)。
   * disabled の `MultiSelect` はドロップダウンを開かず、 選択肢は選択済みチップの
   * ラベル解決にしか使われないため、 業務エリアで絞らず全 9 エリアを選択肢にする
   * (業務エリア外のエリアが共有 state に残っていても数値ではなくエリア名で表示する)。
   */
  disabled?: boolean
  /**
   * `disabled` 時に MultiSelect だけへ出す理由 (板発注 AC-BIN-103)。
   * ⚙ (業務エリアの設定変更) は包まない (= 設定へ行ける導線を止めない)。
   */
  disabledReason?: string
}

/**
 * 選択に残った業務エリア外のエリアを落とした選択。 変える必要が無ければ `undefined`。
 *
 * - 「すべて」 (= 全 9 エリア) は対象外 (業務エリアだけに削ると、 既定状態で業務エリア外の既存札まで
 *   一覧から消えるため)
 * - 落とした結果 0 件になったら「すべて」 に戻す (空集合を出さない)
 *
 * この部品の整理と、 時間前入札の画面 (`IntradayBidding/index.tsx`) が共有する。 部品は開閉式の
 * カードの中にあって閉じている間は描かないので、 画面でも同じ整理をする (#12102 レビュー)。
 */
export const pruneToSelectableAreas = (
  selectedAreas: ReadonlySet<AreaCode>,
  selectableAreas: readonly AreaCode[],
): Set<AreaCode> | undefined => {
  if (JEPX_AREA_CODES.every((code) => selectedAreas.has(code))) return undefined
  const kept = selectableAreas.filter((code) => selectedAreas.has(code))
  if (kept.length === selectedAreas.size) return undefined
  return new Set(kept.length > 0 ? kept : JEPX_AREA_CODES)
}

/**
 * 時間前入札の全体フィルタ (AC-BIN-102) のエリア選択 (= 複数選択 MultiSelect、
 * design intraday.html `#intraday-area-ms` 準拠)。 画面の最上部の全体の条件 ([[IntradayFilterToolbar]]) の
 * カードの中で、 入札追加・入札履歴・約定履歴が共有する。
 * 可視ラベル (「エリア」) は呼び出し側の行レイアウトに置く。
 *
 * 選択肢は JEPX 9 エリアのうち業務エリア設定で選んでいるエリア
 * (`useBusinessAreaFilter`)。 取引できるかどうか (`ListTradableAreas`) では絞らない
 * (フィルタは既存の札を見るためのもので、 取引不可エリアの札が一覧から消えるのを防ぐ。
 * 取引不可は札作成モーダル側の判定のまま)。
 */
export const IntradayAreaMultiSelect = ({
  selectedAreas,
  onAreasChange,
  disabled = false,
  disabledReason,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { selectableAreaCodes: businessAreaCodes } =
    useBusinessAreaFilter(JEPX_AREA_CODES)

  /**
   * 選択肢に出すエリア (= 業務エリア設定で選んでいるエリア)。
   *
   * `MultiSelect` は option 単位の `disabled` を持たないため、 業務エリア外は
   * **選択肢に出さない** (conventions.md「業務エリアフィルタの原則」)。
   * 板発注の表示専用 (`disabled`) はラベル解決だけなので全 9 エリアを渡す。
   */
  const selectableAreas = useMemo(
    () => (disabled ? JEPX_AREA_CODES : businessAreaCodes),
    [disabled, businessAreaCodes],
  )

  const areaOptions: MultiSelectOption[] = selectableAreas.map((code) => ({
    label: convertAreaLabel(code),
    value: String(code),
  }))

  /**
   * widget 上の選択値。 **全 9 エリア (= すべて) は空選択として見せる**
   * (design intraday.html の `initIntradayAreaMS` が `selected: []` +
   * placeholder「全エリア」 で開くのと同じ。 9 個の chip を常時並べない)。
   * 並びは canonical な `JEPX_AREA_CODES` (北→南) を SoT にする
   * (`buildSelectedAreasLabel` と同じ規則)。
   */
  const isAllAreas = JEPX_AREA_CODES.every((code) => selectedAreas.has(code))
  const selectedAreaValues = isAllAreas
    ? []
    : JEPX_AREA_CODES.filter((code) => selectedAreas.has(code)).map(String)

  /**
   * 選択に残った業務エリア外のエリアを落とす (= 業務エリア設定の取得前に選べてしまった分、
   * および localStorage から復元した分、 設定変更で対象外になった分)。
   *
   * 取得前は `useBusinessAreaFilter` が全 9 エリアを返すので、 そこで選んだエリアが後から
   * 業務エリア外と判明することがある。 そのまま残すと候補に無い値になり、 `MultiSelect` が
   * ラベルを解決できず数値 chip のまま一覧 / チャート / 札作成 prefill を絞り込み続ける。
   *
   * 「すべて」 (= 全 9 エリア) は対象外。 widget 上は placeholder 表示で数値 chip が出ず、
   * 実集合を業務エリアだけに削ると既定状態で業務エリア外の既存札まで一覧から
   * 消えてしまうため、 全 9 エリアのまま据え置く。 落とした結果 0 件になったときは
   * 「すべて」 に戻す (= この部品からは空集合を出さない。 Props の JSDoc 参照)。
   */
  useEffect(() => {
    if (disabled) return
    const next = pruneToSelectableAreas(selectedAreas, selectableAreas)
    if (next !== undefined) onAreasChange(next)
  }, [disabled, selectableAreas, selectedAreas, onAreasChange])

  const handleAreasChange = (next: string[]): void => {
    // 空選択 = すべて (= 全 9 エリア)。 親・下流には常に実集合を渡し、 この部品からは
    // 「0 件選択」 を発生させない (Props `selectedAreas` の JSDoc 参照)。
    if (next.length === 0) {
      onAreasChange(new Set(JEPX_AREA_CODES))
      return
    }
    onAreasChange(new Set(next.map((v) => Number(v) as AreaCode)))
  }

  const select = (
    // design `#intraday-area-ms` (`min-width:240px; max-width:520px`)。
    // MultiSelect の root は `width:100%` / `min-width:0` で、 枠線はその内側。
    // min/max を root に載せ、 `sizing="fit"` で選んだ chip の分だけ 240–520px に伸ばす
    // (親だけだと空の「すべて」が 240px に届かず、 歯車との間が空く)。
    <Box
      flexShrink={0}
      css={{
        '& > div': {
          width: 'fit-content',
          minWidth: AREA_FILTER_MIN_WIDTH,
          maxWidth: AREA_FILTER_MAX_WIDTH,
        },
      }}
    >
      <MultiSelect
        // [design 差異] design `initIntradayAreaMS` の `ariaLabel` は
        // 「エリア選択」 だが、 隣の可視ラベル (「エリア」) と同じ文言に揃える
        // (SupplyGap `FilterBar` のエリア MultiSelect と同じ作法)。
        aria-label={t('page:biddingIntraday.area.label')}
        disabled={disabled}
        onChange={handleAreasChange}
        options={areaOptions}
        placeholder={t('page:biddingIntraday.area.allAreas')}
        selected={selectedAreaValues}
        sizing="fit"
      />
    </Box>
  )

  return (
    <Flex alignItems="center" flexWrap="wrap" flexShrink={0} gap={2}>
      {disabled && disabledReason !== undefined && disabledReason !== '' ? (
        <ButtonTooltip content={disabledReason}>{select}</ButtonTooltip>
      ) : (
        select
      )}

      {/* 業務エリア設定変更の導線。 SupplyGap `FilterBar` と同じ作り
          (IconButton ghost + gear)。 行き先は個人設定の業務エリアのカード
          (`/settings?focus=business-area`。 design `include-nav.js` の歯車と同じ)。
          開き方はプロトタイプの歯車と同じく新しいタブ (編集中の絞り込みを残す)。
          新しいタブなので SPA の RouterLink ではなく native <a> にする
          (IconButton asChild 経由でも target / rel が DOM に残る)。
          アイコンだけなので、 aria-label と同じ文言を DS Tooltip で出す (IconOnlyButton が持つ)。 */}
      <IconOnlyButton
        flexShrink={0}
        color="Text.midEmphasis"
        asChild
        label={t('page:biddingIntraday.area.settingsLink')}
        size="sm"
        variant="ghost"
      >
        <a
          href={`${routePaths.settings.path}?focus=business-area`}
          rel="noopener noreferrer"
          target="_blank"
        >
          <FaGearRegular boxSize={iconSize.gridInline} aria-hidden="true" />
        </a>
      </IconOnlyButton>
    </Flex>
  )
}
